import React, { useEffect, useState } from "react";
import axios from "axios";
import { errMessage } from "../../lib/errors";
import { MessageSquareQuote, Instagram, Loader2, Copy, Check, Wand2, Image as ImageIcon, Download, Printer, Sparkles } from "lucide-react";
import { menuHighlights } from "../../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function resolveSrc(url) {
  if (!url) return null;
  return url.startsWith("/") ? `${BACKEND}${url}` : url;
}

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard blocked */ }
  };
  return (
    <button
      onClick={copy}
      data-testid={testId}
      className="inline-flex items-center gap-2 text-[0.66rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors"
    >
      {copied ? <Check size={13} className="text-[var(--mm-accent)]" /> : <Copy size={13} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function ReviewReplyCard({ toast }) {
  const [review, setReview] = useState("");
  const [rating, setRating] = useState("5");
  const [tone, setTone] = useState("warm");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (review.trim().length < 3) {
      toast({ title: "Paste the customer's review first" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai/review-reply`, {
        review_text: review.trim(),
        rating: Number(rating),
        tone,
      });
      setReply(data.reply || "");
    } catch (e) {
      toast({ title: "Generation failed", description: errMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="ai-review-reply-card">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquareQuote size={16} className="text-[var(--mm-accent)]" />
        <div className="section-label">Google Review Reply Writer</div>
      </div>
      <p className="text-sm text-[var(--mm-cream-muted)] mb-4">
        Paste a customer review from Google and get a ready-to-post reply written in Mama&apos;s voice.
      </p>
      <textarea
        value={review}
        onChange={(e) => setReview(e.target.value)}
        rows={4}
        placeholder='Paste the review here — e.g. "Best jollof in Brisbane, the pepper soup blew my mind!"'
        data-testid="ai-review-input"
        className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none resize-none"
      />
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <select
          value={rating}
          onChange={(e) => setRating(e.target.value)}
          data-testid="ai-review-rating"
          className="bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        >
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
        </select>
        <select
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          data-testid="ai-review-tone"
          className="bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        >
          <option value="warm">Warm &amp; personal</option>
          <option value="professional">Professional</option>
          <option value="playful">Playful</option>
        </select>
        <button
          onClick={generate}
          disabled={busy}
          data-testid="ai-review-generate"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {busy ? "Writing…" : "Write reply"}
        </button>
      </div>
      {reply && (
        <div className="mt-4 p-4 border border-[var(--mm-accent)]/40 bg-[var(--mm-bg)]" data-testid="ai-review-output">
          <p className="text-sm text-[var(--mm-cream)] leading-relaxed whitespace-pre-wrap">{reply}</p>
          <div className="mt-3 flex justify-end">
            <CopyButton text={reply} testId="ai-review-copy" />
          </div>
        </div>
      )}
    </div>
  );
}

function CaptionCard({ toast }) {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [vibe, setVibe] = useState("fun");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (topic.trim().length < 2) {
      toast({ title: "Tell it what the post is about first" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai/social-caption`, {
        topic: topic.trim(),
        platform,
        vibe,
      });
      setCaption(data.caption || "");
    } catch (e) {
      toast({ title: "Generation failed", description: errMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="ai-caption-card">
      <div className="flex items-center gap-2 mb-2">
        <Instagram size={16} className="text-[var(--mm-accent)]" />
        <div className="section-label">Social Caption Writer</div>
      </div>
      <p className="text-sm text-[var(--mm-cream-muted)] mb-4">
        Instagram &amp; Facebook captions grounded on the real menu — hashtags included.
      </p>
      <input
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="What's the post about? e.g. Attieke Feast Platter weekend special"
        data-testid="ai-caption-topic"
        className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
      />
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          data-testid="ai-caption-platform"
          className="bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        >
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
        </select>
        <select
          value={vibe}
          onChange={(e) => setVibe(e.target.value)}
          data-testid="ai-caption-vibe"
          className="bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        >
          <option value="fun">Fun &amp; hungry</option>
          <option value="heartfelt">Heartfelt / family story</option>
          <option value="urgent">Limited-time urgency</option>
        </select>
        <button
          onClick={generate}
          disabled={busy}
          data-testid="ai-caption-generate"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {busy ? "Writing…" : "Write caption"}
        </button>
      </div>
      {caption && (
        <div className="mt-4 p-4 border border-[var(--mm-accent)]/40 bg-[var(--mm-bg)]" data-testid="ai-caption-output">
          <p className="text-sm text-[var(--mm-cream)] leading-relaxed whitespace-pre-wrap">{caption}</p>
          <div className="mt-3 flex justify-end">
            <CopyButton text={caption} testId="ai-caption-copy" />
          </div>
        </div>
      )}
    </div>
  );
}

function SpecialsPosterCard({ toast }) {
  const [special, setSpecial] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [poster, setPoster] = useState(null); // {url, dish_name, price}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/menu/special`).then(({ data }) => {
      const item = data.item;
      if (item) {
        const staticDish = menuHighlights.find((m) => m.id === item.id) || {};
        setSpecial({
          name: item.name || staticDish.name || `Dish #${item.id}`,
          price: item.price != null ? `$${item.price}` : (staticDish.price || ""),
        });
      }
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai/specials-poster`, {}, { timeout: 180000 });
      setPoster(data);
      toast({ title: "Poster ready", description: `${data.dish_name} — download or print below.` });
    } catch (e) {
      toast({ title: "Poster failed", description: errMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    try {
      const res = await fetch(resolveSrc(poster.url));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `todays-special-poster.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(resolveSrc(poster.url), "_blank");
    }
  };

  const print = () => {
    const src = resolveSrc(poster.url);
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(
      `<html><head><title>Today's Special Poster</title><style>@page{margin:0}body{margin:0}img{width:100%;display:block}</style></head>` +
      `<body><img src="${src}" onload="window.print()" /></body></html>`
    );
    w.document.close();
  };

  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="ai-poster-card">
      <div className="flex items-center gap-2 mb-2">
        <ImageIcon size={16} className="text-[var(--mm-accent)]" />
        <div className="section-label">Today&apos;s Special Poster</div>
      </div>
      <p className="text-sm text-[var(--mm-cream-muted)] mb-4">
        One tap turns today&apos;s special into a print-ready A4 poster — hang it in the window or share it online.
      </p>

      {loaded && !special && (
        <div className="text-sm text-[var(--mm-cream-muted)] border border-dashed border-[var(--mm-line)] p-4" data-testid="ai-poster-no-special">
          No Today&apos;s Special is set. Feature a dish (the <Sparkles size={12} className="inline text-[var(--mm-accent)]" /> button) in the Menu tab first.
        </div>
      )}

      {special && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="text-sm text-[var(--mm-cream)]" data-testid="ai-poster-special-name">
            Today&apos;s special: <span className="font-display italic text-lg text-[var(--mm-accent)]">{special.name}</span>
            {special.price ? <span className="text-[var(--mm-cream-muted)]"> · {special.price}</span> : null}
          </div>
          <button
            onClick={generate}
            disabled={busy}
            data-testid="ai-poster-generate"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {busy ? "Designing… ~30s" : (poster ? "Regenerate poster" : "Generate poster")}
          </button>
        </div>
      )}

      {poster && (
        <div className="mt-5 space-y-3" data-testid="ai-poster-result">
          <img
            src={resolveSrc(poster.url)}
            alt={`Today's special poster — ${poster.dish_name}`}
            className="max-h-[520px] border border-[var(--mm-line)]"
            data-testid="ai-poster-preview"
          />
          <div className="flex flex-wrap gap-3">
            <button onClick={download} data-testid="ai-poster-download" className="btn-outline inline-flex items-center gap-2">
              <Download size={14} /> Download PNG
            </button>
            <button onClick={print} data-testid="ai-poster-print" className="btn-outline inline-flex items-center gap-2">
              <Printer size={14} /> Print
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AiStudioPanel({ toast }) {
  return (
    <div className="space-y-6" data-testid="ai-studio-panel">
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
        <div className="section-label mb-2">AI Studio</div>
        <p className="text-sm text-[var(--mm-cream-muted)]">
          AI assistants that know the real menu — write replies and captions, or design today&apos;s poster. Generate, tweak if you like, then copy or print.
        </p>
      </div>
      <SpecialsPosterCard toast={toast} />
      <ReviewReplyCard toast={toast} />
      <CaptionCard toast={toast} />
    </div>
  );
}
